import React from 'react';
import { Entity } from '@backstage/catalog-model';
import { Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';

import {
  EntityJenkinsContent,
  isJenkinsAvailable,
} from '@backstage-community/plugin-jenkins';

import { EntityLogsIframe } from '../observability/EntityLogsIframe';
import { EntityObservabilityCard } from '../observability/EntityObservabilityCard';
import { EntityConventionLinksCard } from './EntityConventionLinksCard';
import { EntityProvidedApiInlineDefinitions } from './EntityProvidedApiInlineDefinitions';
import { EntitySparrowDeskSelectorBar } from '../sparrowdesk/EntitySparrowDeskSelectorBar';
import { EntitySparrowDeskArgoCDCards, EntitySparrowDeskArgoCDContent } from '../sparrowdesk/EntitySparrowDeskArgoCDContent';
import { EntitySparrowDeskLinksCard } from '../sparrowdesk/EntitySparrowDeskLinksCard';
import { EntitySparrowDeskDeploymentMatrix } from '../sparrowdesk/EntitySparrowDeskDeploymentMatrix';
import { EntitySparrowDeskJenkinsContent } from '../sparrowdesk/EntitySparrowDeskJenkinsContent';
import { EntityAppPipelinesContent, isAppPipelinesEntity } from './EntityAppPipelinesContent';
import { EntitySparrowDeskObservabilityCard } from '../sparrowdesk/EntitySparrowDeskObservabilityCard';
import { EntitySparrowDeskLogsIframe } from '../sparrowdesk/EntitySparrowDeskLogsIframe';
import { ENV_DASHBOARD_ANNOTATION } from '../coalitions';

// Any Component entity that opts into the multi-env dashboard carries
// `sparrow.io/env-dashboard: <coalitionKey>`. The switch cases below
// don't care WHICH coalition — they just render the coalition-agnostic
// components when the annotation is present. Coalition-specific data
// (envs, URL templates, apps) comes from envDashboards.<coalitionKey>
// in app-config.
const isEnvDashboardEntity = (entity: {
  metadata?: { annotations?: Record<string, string> };
}) => Boolean(entity.metadata?.annotations?.[ENV_DASHBOARD_ANNOTATION]);

import {
  isArgocdAvailable,
  EntityArgoCDOverviewCard,
  EntityArgoCDHistoryCard
} from '@roadiehq/backstage-plugin-argo-cd';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

// Per-entity opt-out of tabs / cards that don't apply (e.g. a library or a
// browser extension has no Jenkins pipeline or logs):
//   sparrow.io/hide: jenkins,logs,observability,argocd,api,dependencies,docs
// `config` and `secrets` drop the guessed Config / Secrets links from the
// Overview Links card (EntityConventionLinksCard).
const HIDE_ANNOTATION = 'sparrow.io/hide';
const hides = (part: string) => (entity: Entity) =>
  (entity.metadata.annotations?.[HIDE_ANNOTATION] ?? '')
    .split(',')
    .map(p => p.trim().toLowerCase())
    .includes(part);
const shows = (part: string) => (entity: Entity) => !hides(part)(entity);

const cicdContent = (
  <EntitySwitch>
      {/* SparrowDesk coalition apps deploy to 13 envs; a single
          jenkins.io/job-full-name annotation can only point at one env.
          Our custom tab reads the ?env= URL context and renders BE/FE
          deep-links for the selected env. Order matters — first match
          wins, so this must precede the generic community case. */}
      <EntitySwitch.Case if={isEnvDashboardEntity}>
        <EntitySparrowDeskJenkinsContent />
      </EntitySwitch.Case>
      {/* app-bootstrap apps: three pipelines on the staging AND production
          Jenkins in one view (sparrow.io/jenkins-folder annotation). The
          community plugin below can only show one Jenkins instance. */}
      <EntitySwitch.Case if={isAppPipelinesEntity}>
        <EntityAppPipelinesContent />
      </EntitySwitch.Case>
      <EntitySwitch.Case if={isJenkinsAvailable}>
        <EntityJenkinsContent />
      </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}

    {/* SparrowDesk env + app selector bar — rendered only for entities
        opted in via `sparrow.io/env-dashboard: sparrowdesk`. */}
    <EntitySwitch>
      <EntitySwitch.Case if={isEnvDashboardEntity}>
        <Grid item xs={12}>
          <EntitySparrowDeskSelectorBar />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Row 1: About + Links — the two identity cards */}
    <Grid item md={6} xs={12}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityConventionLinksCard />
    </Grid>

    {/* Env-scoped 6-link card for SparrowDesk apps only. */}
    <EntitySwitch>
      <EntitySwitch.Case if={isEnvDashboardEntity}>
        <Grid item md={6} xs={12}>
          <EntitySparrowDeskLinksCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Row 2: ArgoCD deployment status + Observability deep-links.
        Env-dashboard entities: the selected env's ArgoCD app (follows the selector above). */}
    <EntitySwitch>
      <EntitySwitch.Case if={isEnvDashboardEntity}>
        <Grid item md={6} xs={12}>
          <EntitySparrowDeskArgoCDCards compact />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
        <Grid item md={6} xs={12}>
          <EntityArgoCDOverviewCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    {/* The generic ObservabilityCard's URL builder hardcodes datasource
        / cluster / namespace from BuOpsConfig — wrong for SparrowDesk
        coalition apps. For opted-in entities render our env-scoped
        SparrowDesk variant instead. */}
    <EntitySwitch>
      <EntitySwitch.Case if={e => isEnvDashboardEntity(e) && shows('observability')(e)}>
        <Grid item md={6} xs={12}>
          <EntitySparrowDeskObservabilityCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case if={e => !isEnvDashboardEntity(e) && shows('observability')(e)}>
        <Grid item md={6} xs={12}>
          <EntityObservabilityCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Row 3: SonarQube (if available) — Jenkins latest-run card
        dropped because the community plugin's `EntityLatestJenkinsRunCard`
        is hardwired for multi-branch pipelines: it takes a required
        `branch` prop and appends `/job/${branch}` to the API URL,
        which 404s against our single-branch jobs. The Jenkins tab
        already covers per-entity CI details. */}
    <EntitySwitch>
      <EntitySwitch.Case if={isSonarQubeAvailable}>
        <Grid item md={6} xs={12}>
          <EntitySonarQubeCard variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/jenkins" title="Jenkins" if={shows('jenkins')}>
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="ArgoCD"
      if={e => (Boolean(isArgocdAvailable(e)) || isEnvDashboardEntity(e)) && shows('argocd')(e)}
    >
      <EntitySwitch>
        {/* Env-dashboard entities: the selected env's ArgoCD app (env selector on top). */}
        <EntitySwitch.Case if={isEnvDashboardEntity}>
          <EntitySparrowDeskArgoCDContent />
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EntityArgoCDOverviewCard />
            </Grid>
            <Grid item xs={12}>
              <EntityArgoCDHistoryCard />
            </Grid>
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs" if={shows('logs')}>
      <EntitySwitch>
        <EntitySwitch.Case if={isEnvDashboardEntity}>
          <EntitySparrowDeskLogsIframe />
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <EntityLogsIframe />
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API" if={shows('api')}>
      <Grid container spacing={3} alignItems="stretch">
        {/* Row 1: compact list(s) of provided + consumed APIs.
            Consumed APIs card only shows for entities that actually
            consume something — skips the "This component does not
            consume any APIs" placeholder for the majority of services. */}
        <Grid item xs={12} md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case
            if={e => Boolean((e.spec?.consumesApis as unknown[])?.length)}
          >
            <Grid item xs={12} md={6}>
              <EntityConsumedApisCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        {/* Row 2: full API definition (Swagger UI etc.) for each
            provided API rendered inline. Zero clicks to reach the
            actual endpoints and schemas. */}
        <EntityProvidedApiInlineDefinitions />
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies" if={shows('dependencies')}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Multi-env deployment matrix — only shown for SparrowDesk apps that
        opt in via the sparrow.io/env-dashboard annotation. */}
    <EntityLayout.Route
      path="/deployments"
      title="Deployments"
      if={isEnvDashboardEntity}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntitySparrowDeskSelectorBar />
        </Grid>
        <Grid item xs={12}>
          <EntitySparrowDeskDeploymentMatrix />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={shows('docs')}>
      {techdocsContent}
    </EntityLayout.Route>

  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/jenkins" title="Jenkins" if={shows('jenkins')}>
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/argocd"
      title="ArgoCD"
      if={e => (Boolean(isArgocdAvailable(e)) || isEnvDashboardEntity(e)) && shows('argocd')(e)}
    >
      <EntitySwitch>
        {/* Env-dashboard entities: the selected env's ArgoCD app (env selector on top). */}
        <EntitySwitch.Case if={isEnvDashboardEntity}>
          <EntitySparrowDeskArgoCDContent />
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EntityArgoCDOverviewCard />
            </Grid>
            <Grid item xs={12}>
              <EntityArgoCDHistoryCard />
            </Grid>
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route path="/logs" title="Logs" if={shows('logs')}>
      <EntitySwitch>
        <EntitySwitch.Case if={isEnvDashboardEntity}>
          <EntitySparrowDeskLogsIframe />
        </EntitySwitch.Case>
        <EntitySwitch.Case>
          <EntityLogsIframe />
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies" if={shows('dependencies')}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={shows('docs')}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

// Tools, libraries, mobile apps (the default page) get a Jenkins tab only when
// they have pipelines: a Jenkins annotation, app pipelines, or env-dashboard
// `sparrow.io/<env>-jenkins-url` links (the tab then follows the env selector).
const hasPipelines = (entity: Entity) =>
  Boolean(isJenkinsAvailable(entity)) ||
  isAppPipelinesEntity(entity) ||
  (isEnvDashboardEntity(entity) &&
    Object.keys(entity.metadata.annotations ?? {}).some(k => k.startsWith('sparrow.io/') && k.endsWith('-jenkins-url')));

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/jenkins"
      title="Jenkins"
      if={e => hasPipelines(e) && shows('jenkins')(e)}
    >
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs" if={shows('docs')}>
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
